import { FC, useEffect, useState, useCallback } from "react";
import { useDispatch, useSelector } from "react-redux";
import toast, { Toaster } from "react-hot-toast";
import { useLocation } from "react-router-dom";

import clsx from "clsx";

import {
  actionGetEmployeesFiltersLogin,
  actionGetEmployeesFiltersRoles,
  actionTableEmployeesList,
  actionGetRoles,
} from "../../../redux/action/accessRights/actionUsersList";

import {
  reducerUsersInputValues,
  reducerSelectedUserId,
  reducerUsersStatus,
  reducerSelectedUser,
  reducerGetUsersClaims,
} from "../../../redux/reducer/accessRights/reducers/usersList";
import { generateStyles } from "../../../utils/generateToastStyles";
import { AppDispatch } from "../../../redux/reducer/store";

import { formatReports } from "../../../utils/formatReports";

import Select from "../../custom/select/select";
import LimitSelect from "../../custom/limitSelect/limitSelect";
import ToastPopup from "../../custom/toastPopup/toastPopup";
import Pagination from "../../pagination/pagination";
import RolesEmployeesModal from "../../rolesModal/rolesEmployeesModal";
import Loader from "../../custom/loader/loader";

import "../table.scss";
import { IUserOptions } from "../../../types/interfaces";

interface IClaimsData {
  claimsCol: number;
  fired: boolean;
  familyName?: string;
  givenName?: string;
  middleName?: string;
  id: number;
  role: { [key: string]: string };
  claims: { id: number; name: string }[];
  login: string;
}

const IS_VISIBLE_SIDEBAR = process.env.REACT_APP_IS_VISIBLE_SIDEBAR;

const requestStatusOptions = [
  {
    status: 200,
    successMessage: "Новый клиент успешно добавлен!",
  },
  {
    status: 0,
    errorMessage: "При добавлении клиента произошла ошибка!",
  },
  {
    status: "deleteSuccess",
    successMessage: "Клиент успешно удален!",
  },
  {
    status: "deleteError",
    errorMessage: "При удалении клиента произошла ошибка!",
  },
  {
    status: "invalidEmail",
    errorMessage: "Некорректный адрес электронной почты!",
  },
  {
    status: "noData",
    errorMessage: "Необходимо заполнить недостающие значения!",
  },
  {
    status: "updateSuccess",
    successMessage: "Данные клиента успешно обновлены!",
  },
  {
    status: "updateError",
    errorMessage: "При обновлении данных клиента произошла ошибка!",
  },
];

const booleanOptions = [
  { value: true, label: "Да" },
  { value: false, label: "Нет" },
];

const TableAdminUsers: FC = () => {
  const dispatch = useDispatch<AppDispatch>();

  const location = useLocation();

  const {
    userInputValues,
    selectedUserId,
    clientsStatus,
    usersFilters,
    checkboxes,
    usersCount,
    limit,
    pages,
    filtredEmployeesData,
  } = useSelector((state: any) => state.roles.usersList);
  const { isOpenSidebar } = useSelector(
    (state: IUserOptions) => state.user.user
  );
  const { claims } = useSelector((state: IUserOptions) => state.user.user.data);
  const [isInputChange, setIsInputChange] = useState<boolean>(false);
  const [isPending, setIsPending] = useState<boolean>(false);
  const [isOpenModal, setIsOpenModal] = useState<boolean>(false);
  const [modalTitle, setModalTitle] = useState<string>("");
  const [userData, setUserData] = useState<{ id: number; role: string } | null>(
    null
  );
  const [currentPage, setCurrentPage] = useState<number>(1);
  const [startPage, setStartPage] = useState<number>(1);
  const [isHeaderCheckboxChecked, setIsHeaderCheckboxChecked] =
    useState<boolean>(false);
  const [isCheckedRows, setIsCheckedRows] = useState<boolean[]>(checkboxes);
  const [isVisibleDataList, setIsVisibleDataList] = useState<boolean>(false);
  const [searchTerm, setSearchTerm] = useState<string[]>([]);

  const actualLimit =
    JSON.parse(localStorage.getItem("pageLimit") as string) || limit;

  const checkboxList = (check: boolean) => {
    const updatedCheckedRows =
      filtredEmployeesData &&
      filtredEmployeesData.reduce(
        (acc: { [key: number]: boolean }, user: IClaimsData) => {
          const userId = user?.id;
          acc[userId] = check;
          return acc;
        },
        {}
      );
    dispatch(reducerSelectedUserId(formatReports(updatedCheckedRows)));
    setIsCheckedRows(updatedCheckedRows);
  };
  useEffect(() => {
    requestStatusOptions.forEach(
      (option: {
        status: number | string;
        successMessage?: string;
        errorMessage?: string | undefined;
      }) => {
        if (clientsStatus === option.status) {
          toast.dismiss();

          toast(
            (t) => (
              <ToastPopup
                title={
                  option.successMessage
                    ? option.successMessage
                    : option.errorMessage
                }
                isSuccess={option.successMessage ? true : false}
                toast={toast}
                t={t}
              />
            ),
            generateStyles(option.successMessage ? true : false)
          );
        }
      }
    );

    setTimeout(() => {
      dispatch(reducerUsersStatus(null));
    }, 5000);
  }, [dispatch, clientsStatus]);

  useEffect(() => {
    if (selectedUserId && !selectedUserId.length) {
      setIsCheckedRows(filtredEmployeesData && checkboxes);
      setIsHeaderCheckboxChecked(false);
    }
    // eslint-disable-next-line
  }, [checkboxes, filtredEmployeesData, selectedUserId]);

  useEffect(() => {
    if (filtredEmployeesData) {
      checkboxList(false);
    }
    // eslint-disable-next-line
  }, [dispatch, filtredEmployeesData]);

  useEffect(() => {
    if (isHeaderCheckboxChecked && filtredEmployeesData) {
      checkboxList(isHeaderCheckboxChecked);
    }
    // eslint-disable-next-line
  }, [dispatch, isHeaderCheckboxChecked, filtredEmployeesData]);

  const handleHeaderCheckboxChange = () => {
    setIsHeaderCheckboxChecked(!isHeaderCheckboxChecked);
    if (isHeaderCheckboxChecked) {
      checkboxList(false);
    }
  };
  const handleRowCheckboxChange = (el: IClaimsData) => {
    const userId = el.id;
    setIsCheckedRows((prevCheckedRows: boolean[]) => ({
      ...prevCheckedRows,
      [userId]: !prevCheckedRows[userId],
    }));
    const updatedSelectedVacancies = formatReports({
      ...isCheckedRows,
      [userId]: !isCheckedRows[userId],
    });
    dispatch(reducerSelectedUserId(updatedSelectedVacancies));
    dispatch(reducerSelectedUser(el));
    if (updatedSelectedVacancies.length === filtredEmployeesData.length) {
      setIsHeaderCheckboxChecked(true);
    } else {
      setIsHeaderCheckboxChecked(false);
    }
  };

  useEffect(() => {
    const handleDocumentClick = (
      event: React.ChangeEvent<HTMLInputElement> | Event
    ): void => {
      const inputEvent = event as React.ChangeEvent<HTMLInputElement>;
      if (inputEvent.target.name !== "login") {
        setIsVisibleDataList(false);
        setIsPending(true);
        dispatch(actionTableEmployeesList(limit, pages, true)).then(() => {
          setIsPending(false);
        });
        setIsInputChange(false);
        resetPages();
      }
    };

    if (isInputChange) {
      document.addEventListener("click", handleDocumentClick);
      return () => {
        document.removeEventListener("click", handleDocumentClick);
      };
    } else {
      document.removeEventListener("click", handleDocumentClick);
    }
  }, [dispatch, isInputChange, limit, pages]);

  const resetPages = () => {
    setCurrentPage(1);
    setStartPage(1);
  };

  const handleKeyPress = (event: React.KeyboardEvent<HTMLInputElement>) => {
    if (event.key === "Enter") {
      setIsPending(true);
      dispatch(actionTableEmployeesList(limit, pages, true)).then(() => {
        setIsPending(false);
      });
      setIsInputChange(false);
      resetPages();
    }
  };

  const handleFilterInputChange = (
    event: React.ChangeEvent<HTMLInputElement>
  ) => {
    const { name, value } = event.target;
    if (value === "") {
      const updatedInputValues = { ...userInputValues };
      delete updatedInputValues[name];
      dispatch(reducerUsersInputValues(updatedInputValues));
      setSearchTerm([]);
      setIsVisibleDataList(false);
    } else {
      if (name === "login") {
        setIsVisibleDataList(true);
        const filteredOptions = usersFilters?.logins?.filter((option: string) =>
          option?.toLowerCase()?.includes(value?.toLowerCase())
        );
        setSearchTerm(filteredOptions);
      }
      dispatch(
        reducerUsersInputValues({
          ...userInputValues,
          [name]: value,
        })
      );
    }
    setIsInputChange(true);
  };

  const handleChange = (newLimit: number, newOffset: number) => {
    setIsPending(true);
    dispatch(actionTableEmployeesList(newLimit, newOffset, true)).then(() => {
      setIsPending(false);
    });
    localStorage.setItem("pageLimit", JSON.stringify(newLimit));
  };

  const selectChange = (
    event: { value: string; label: string },
    name: string
  ) => {
    const value = event.value;
    if (value === "") {
      const updatedInputValues = { ...userInputValues };
      delete updatedInputValues[name];
      dispatch(reducerUsersInputValues(updatedInputValues));
    } else {
      dispatch(
        reducerUsersInputValues({
          ...userInputValues,
          [name]: value,
        })
      );
    }
    setIsInputChange(true);
  };

  const handleChangeLimits = (option: { value: number }) => {
    handleChange(option.value, 0);
    setCurrentPage(1);
    setStartPage(1);
  };

  const handleCloseModal = useCallback(() => {
    setIsOpenModal(false);
    setModalTitle("");
    dispatch(actionTableEmployeesList(limit, pages, true));
    // eslint-disable-next-line
  }, [dispatch]);

  const handleOpenModal = (
    name: string,
    claim: { id: number; name?: string }[],
    id: number,
    role: { [key: string]: string }
  ) => {
    setIsOpenModal(true);
    dispatch(reducerGetUsersClaims(claim));
    setModalTitle(name);
    setUserData({ id: id, role: Object.values(role).toString() });
  };

  const handleClickDataList = (item: string) => {
    dispatch(
      reducerUsersInputValues({
        ...userInputValues,
        login: item,
      })
    );
    setIsInputChange(true);
    setIsVisibleDataList(false);
  };

  useEffect(() => {
    setIsPending(true);
    dispatch(reducerUsersInputValues(null));
    dispatch(actionGetEmployeesFiltersLogin());
    dispatch(actionGetEmployeesFiltersRoles());
    dispatch(actionTableEmployeesList(limit, pages, true)).then(() => {
      dispatch(actionGetRoles());
      setIsPending(false);
    });
    // eslint-disable-next-line
  }, []);

  useEffect(() => {
    toast.dismiss();
  }, [location]);

  return (
    <>
      <div className="loader__wrapper">{isPending && <Loader />}</div>
      <div
        className={clsx(
          JSON.parse(IS_VISIBLE_SIDEBAR as string) &&
            isOpenSidebar &&
            "active__table",
          !isOpenSidebar && "closed__table",
          "table"
        )}
      >
        {clientsStatus && clientsStatus !== null && (
          <Toaster
            position="top-center"
            toastOptions={{
              duration: 5000,
            }}
          />
        )}

        <div className="filters__container__mobile">
          <div className="filters__item__mobile">
            <div className="filter__number_block">
              <input
                value={userInputValues?.givenName || ""}
                onChange={handleFilterInputChange}
                onKeyPress={handleKeyPress}
                placeholder=" "
                name="givenName"
                type="text"
              />
              <label>ФИО</label>
            </div>
          </div>
          <div className="filters__item__mobile">
            <div className="filter__number_block">
              <input
                value={userInputValues?.login || ""}
                onChange={handleFilterInputChange}
                onKeyPress={handleKeyPress}
                placeholder=" "
                name="login"
                type="text"
                autoComplete={"off"}
              />
              <label>Логин</label>
            </div>
            {isVisibleDataList && searchTerm.length > 0 && (
              <ul className="dataList">
                {usersFilters &&
                  usersFilters?.logins &&
                  searchTerm.map((item: string, index: number) => (
                    <li
                      className="dataItem"
                      key={index}
                      onClick={() => {
                        handleClickDataList(item);
                      }}
                    >
                      {item}
                    </li>
                  ))}
              </ul>
            )}
          </div>

          <div className="filters__item__mobile">
            <div
              className={clsx("filter__number_block")}
            >
              <Select
                options={usersFilters?.roles}
                propsChange={(event: { value: string; label: string }) =>
                  selectChange(event, "role")
                }
                placeholder={"Роль"}
                className="users__filter"
                name="role"
                span="Роль"
                isShowPlaceholder={userInputValues && userInputValues?.role}
              />
            </div>
          </div>
          <div className="filters__item__mobile">
            <div
              className={clsx("filter__number_block")}
            >
              <Select
                options={booleanOptions}
                propsChange={(event: { value: string; label: string }) =>
                  selectChange(event, "fired")
                }
                placeholder="Уволен"
                className="users__filter"
                name="fired"
                span="Уволен"
                isShowPlaceholder={
                  userInputValues &&
                  (userInputValues?.fired || userInputValues?.fired === false)
                }
              />
            </div>
          </div>
          <div className="filters__item__mobile">
            <div className="filter__number_block">
              <input
                name="claimsCol"
                placeholder=" "
                type="number"
                onChange={handleFilterInputChange}
                onKeyPress={handleKeyPress}
                min={0}
              />
              <label>Кол-во клейм</label>
            </div>
          </div>
        </div>
        <div className="table__wrapper">
          <table>
            <thead className="table__header__filters">
              <tr className="clients__list_head">
                <th className="reports__checkbox_block reports__checkbox_block-small">
                  {(claims.includes("admin") ||
                    claims.includes("claim:employee:delete")) && (
                    <input
                      onChange={handleHeaderCheckboxChange}
                      checked={isHeaderCheckboxChecked}
                      type="checkbox"
                    />
                  )}
                </th>
                <th>
                  <div className="filter__number_block">
                    <input
                      value={userInputValues?.givenName || ""}
                      onChange={handleFilterInputChange}
                      onKeyPress={handleKeyPress}
                      placeholder=" "
                      name="givenName"
                      type="text"
                    />
                    <label>ФИО</label>
                  </div>
                </th>
                <th>
                  <div className="filter__number_block">
                    <input
                      value={userInputValues?.login || ""}
                      onChange={handleFilterInputChange}
                      onKeyPress={handleKeyPress}
                      placeholder=" "
                      name="login"
                      type="text"
                      autoComplete={"off"}
                    />
                    <label>Логин</label>
                  </div>
                  {isVisibleDataList && searchTerm.length > 0 && (
                    <ul className="dataList">
                      {usersFilters &&
                        usersFilters?.logins &&
                        searchTerm.map((item: string, index: number) => (
                          <li
                            className="dataItem"
                            key={index}
                            onClick={() => {
                              handleClickDataList(item);
                            }}
                          >
                            {item}
                          </li>
                        ))}
                    </ul>
                  )}
                </th>

                <th>
                  <div
                    className={clsx("filter__number_block")}
                  >
                    <Select
                      options={usersFilters?.roles}
                      propsChange={(event: { value: string; label: string }) =>
                        selectChange(event, "role")
                      }
                      placeholder={"Роль"}
                      className="users__filter"
                      name="role"
                      span="Роль"
                      isShowPlaceholder={
                        userInputValues && userInputValues?.role
                      }
                    />
                  </div>
                </th>
                <th>
                  <div
                    className={clsx("filter__number_block")}
                  >
                    <Select
                      options={booleanOptions}
                      propsChange={(event: { value: string; label: string }) =>
                        selectChange(event, "fired")
                      }
                      placeholder="Уволен"
                      className="users__filter"
                      name="fired"
                      span="Уволен"
                      isShowPlaceholder={
                        userInputValues &&
                        (userInputValues?.fired ||
                          userInputValues?.fired === false)
                      }
                    />
                  </div>
                </th>
                <th>
                  <div className="filter__number_block">
                    <input
                      name="claimsCol"
                      placeholder=" "
                      type="number"
                      onChange={handleFilterInputChange}
                      onKeyPress={handleKeyPress}
                      min={0}
                    />
                    <label>Кол-во клейм</label>
                  </div>
                </th>
              </tr>
            </thead>
            <tbody>
              {filtredEmployeesData &&
                filtredEmployeesData.map((elem: IClaimsData, index: number) => {
                  const name = [
                    elem?.familyName,
                    elem?.givenName,
                    elem?.middleName,
                  ].join(" ");
                  return (
                    <tr key={index}>
                      <td>
                        {(claims.includes("admin") ||
                          claims.includes("claim:employee:delete")) && (
                          <input
                            onChange={() => handleRowCheckboxChange(elem)}
                            checked={
                              (isCheckedRows && isCheckedRows[elem.id]) || false
                            }
                            className="relative__elem"
                            type="checkbox"
                          />
                        )}
                      </td>
                      <td className="error__td">
                        <span>{name}</span>
                      </td>
                      <td className="error__td">
                        <span>{elem.login}</span>
                      </td>
                      <td className="error__td">
                        <span>{Object.values(elem.role)}</span>
                      </td>
                      <td>
                        <span>{elem.fired ? "Да" : "Нет"}</span>
                      </td>
                      <td>{elem.claimsCol}</td>
                      <td className="invisible__reports_cell">
                        <span
                          className="link__tr"
                          style={{
                            zIndex: 1,
                            cursor: `${
                              claims.includes("admin") ||
                              claims.includes("claim:employee:list")
                                ? "pointer"
                                : "auto"
                            }`,
                          }}
                          onClick={() =>
                            (claims.includes("admin") ||
                              claims.includes("claim:employee:list")) &&
                            handleOpenModal(
                              `${name} (${Object.values(elem.role)})`,
                              elem.claims,
                              elem.id,
                              elem.role
                            )
                          }
                        ></span>
                      </td>
                    </tr>
                  );
                })}
            </tbody>
          </table>
        </div>
        <div className="pagination">
          <div className="pagination__count">Всего: {usersCount}</div>
          <div className="pagination__str">
            <span>Отображать строки</span>
            <LimitSelect limit={actualLimit} propsChange={handleChangeLimits} />
          </div>
          <Pagination
            setCurrentPage={setCurrentPage}
            setStartPage={setStartPage}
            handleChange={handleChange}
            currentPage={currentPage}
            count={usersCount}
            startPage={startPage}
            limit={actualLimit}
          />
        </div>
      </div>
      {isOpenModal && userData !== null && (
        <RolesEmployeesModal
          onClose={handleCloseModal}
          title={modalTitle}
          id={userData.id}
          role={userData.role}
        />
      )}
    </>
  );
};

export default TableAdminUsers;
